import { useCallback, useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Avatar, Button, Dropdown, DropdownItem, DropdownSeparator, Logo } from '../components/ui';
import { StorageMeter } from '../components/StorageMeter';
import { WorkspaceSwitcher } from '../components/WorkspaceSwitcher';
import { NotificationBell } from '../components/NotificationBell';
import { GlobalSearch } from '../components/GlobalSearch';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { useT } from '../i18n';
import { useWorkspace } from '../hooks';
import { api } from '../lib/api';
import { cn } from '../lib/cn';
import {
  groupForPath,
  groupedNavigationFor,
  mobileNavigationFor,
  resolveWorkspaceRoute,
  type NavGroup,
  type NavSection,
} from '../lib/navigation';
import { useSession } from '../lib/session';

/**
 * Estructura de la aplicación autenticada.
 *
 * Un único armazón sirve a Personal, Family y Business: sólo cambia el menú,
 * porque son el mismo motor con distinto workspace. Duplicar el layout por
 * producto obligaría a arreglar cada cosa dos veces.
 *
 * Qué secciones ofrece cada espacio se decide en `lib/navigation`, no aquí: la
 * misma lista que pinta el menú es la que dice si la pantalla abierta sigue
 * existiendo después de cambiar de espacio.
 */
export function AppLayout() {
  const { session, clear } = useSession();
  const { active, kind, workspaceId } = useWorkspace();
  const rol = active?.role ?? null;
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const t = useT();
  const [menuAbierto, setMenuAbierto] = useState(false);

  const grupos = groupedNavigationFor(kind, rol);
  const itemsMovil = mobileNavigationFor(kind, rol);
  const grupoActivo = groupForPath(pathname);

  /*
   * Grupos plegados.
   *
   * Se recuerda lo plegado y no lo abierto: así un grupo nuevo aparece abierto
   * para todo el mundo, incluso para quien ya tenía una preferencia guardada.
   * Al revés, añadir una sección la escondería a quien lleva meses usando la
   * aplicación, que es justo a quien no se le debe mover el sitio.
   */
  const [plegados, setPlegados] = useState<Set<NavGroup>>(leerPlegados);

  const alternar = useCallback((grupo: NavGroup) => {
    setPlegados((previos) => {
      const siguientes = new Set(previos);
      if (!siguientes.delete(grupo)) siguientes.add(grupo);
      guardarPlegados(siguientes);
      return siguientes;
    });
  }, []);

  /*
   * El grupo de la pantalla abierta se despliega solo.
   *
   * Quien plegó «Espacio» y entra en la papelera desde un enlace o desde el
   * buscador se quedaba con el menú sin ninguna marca de dónde está: la sección
   * activa existía, pero dentro de un grupo cerrado.
   */
  useEffect(() => {
    if (!grupoActivo) return;
    setPlegados((previos) => {
      if (!previos.has(grupoActivo)) return previos;
      const siguientes = new Set(previos);
      siguientes.delete(grupoActivo);
      guardarPlegados(siguientes);
      return siguientes;
    });
  }, [grupoActivo]);

  /*
   * Cambiar de espacio con una pantalla de empresa abierta.
   *
   * El menú se rehace solo, pero el contenido no: quien pasaba de su empresa a
   * su espacio personal desde «Equipo» se quedaba mirando una pantalla que en
   * personal no existe. Se le devuelve al inicio, que sí es suyo.
   */
  const destino = resolveWorkspaceRoute(pathname, kind);
  useEffect(() => {
    if (destino) navigate(destino, { replace: true });
  }, [destino, navigate]);

  async function salir() {
    await api('/api/auth/logout', { method: 'POST' }).catch(() => undefined);
    clear();
    navigate('/login');
  }

  if (!session) return null;

  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[16rem_1fr]">
      <a href="#main" className="skip-link">
        {t('nav.skipToContent')}
      </a>

      {/* Barra lateral: fija en escritorio, cajón deslizante en móvil. */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-line/50 bg-surface/80 backdrop-blur-xl',
          'transition-transform lg:static lg:translate-x-0',
          menuAbierto ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex h-16 items-center px-4">
          {/* Dentro de la aplicación el logo vuelve al dashboard, no a la landing. */}
          <Logo to="/dashboard" />
        </div>

        <div className="px-3">
          <WorkspaceSwitcher />
        </div>

        <nav className="mt-4 flex-1 space-y-1 overflow-y-auto px-3" aria-label={t('nav.sections')}>
          {grupos.map((grupo) => (
            <GrupoDelMenu
              key={grupo.id}
              grupo={grupo}
              abierto={!plegados.has(grupo.id)}
              onAlternar={() => alternar(grupo.id)}
              onNavegar={() => setMenuAbierto(false)}
            />
          ))}
        </nav>

        <div className="border-t border-line/50 p-3">
          <StorageMeter workspaceId={workspaceId} compact showUpgrade />
        </div>
      </aside>

      {menuAbierto ? (
        <button
          type="button"
          aria-label={t('nav.closeMenu')}
          className="fixed inset-0 z-30 bg-black/60 lg:hidden"
          onClick={() => setMenuAbierto(false)}
        />
      ) : null}

      <div className="flex min-h-dvh flex-col">
        <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-line/40 bg-background/70 px-4 backdrop-blur-xl">
          <Button
            variant="ghost"
            size="sm"
            className="lg:hidden"
            aria-label={t('nav.openMenu')}
            onClick={() => setMenuAbierto(true)}
          >
            ☰
          </Button>

          <GlobalSearch />

          <div className="ml-auto flex items-center gap-1">
            <NotificationBell />

            <LanguageSwitcher compact />

            <Dropdown
              label={t('nav.account')}
              trigger={() => (
                <Avatar
                  name={`${session.user.firstName ?? ''} ${session.user.lastName ?? ''}`.trim()}
                  email={session.user.email}
                  src={session.user.avatarUrl}
                />
              )}
              // Quién eres no es una opción del menú: va en la cabecera, fuera
              // del `role="menu"`, que sólo admite opciones como hijos.
              header={
                <>
                  <p className="truncate text-sm font-medium">
                    {session.user.firstName ?? session.user.email}
                  </p>
                  <p className="truncate text-xs text-muted">{session.user.email}</p>
                </>
              }
            >
              {({ close }) => (
                <>
                  <DropdownItem
                    onClick={() => {
                      close();
                      navigate('/settings');
                    }}
                  >
                    {t('nav.settings')}
                  </DropdownItem>
                  <DropdownItem
                    onClick={() => {
                      close();
                      navigate('/settings/subscription');
                    }}
                  >
                    {t('settings.subscription')}
                  </DropdownItem>
                  {session.user.isPlatformAdmin ? (
                    <DropdownItem
                      onClick={() => {
                        close();
                        navigate('/admin');
                      }}
                    >
                      {t('nav.platformAdmin')}
                    </DropdownItem>
                  ) : null}
                  <DropdownSeparator />
                  <DropdownItem danger onClick={() => void salir()}>
                    {t('nav.signOut')}
                  </DropdownItem>
                </>
              )}
            </Dropdown>
          </div>
        </header>

        <main
          id="main"
          className="flex-1 px-4 pb-[calc(6rem+env(safe-area-inset-bottom))] pt-6 lg:px-8 lg:pb-10"
        >
          <Outlet />
        </main>

        {/*
          Navegación inferior: en móvil el pulgar no llega a la esquina.

          Tres detalles que la hacían inservible antes y que no son adorno:

          - **`grid-cols-*` según cuántas hay.** Estaba fijo en cinco, así que
            en un espacio de empresa —donde son cuatro— quedaba una columna
            vacía y las cuatro pestañas se iban a la izquierda.
          - **Una línea y truncado.** Cada columna mide 78 px en un teléfono de
            390: «Enlaces compartidos» se partía en dos y la segunda línea se
            salía de la barra por abajo, encima del contenido. Por eso los
            nombres cortos, y por eso `truncate` como red de seguridad para el
            idioma que se añada mañana.
          - **`pb-[env(safe-area-inset-bottom)]`.** Instalada en un iPhone, la
            barra del sistema —la rayita del gesto de inicio— se come los
            últimos 34 px de la pantalla: sin este relleno, las pestañas quedan
            debajo y no se pueden tocar.
        */}
        <nav
          className={cn(
            'fixed inset-x-0 bottom-0 z-20 grid border-t border-line/50 bg-surface/90 backdrop-blur-xl lg:hidden',
            'pb-[env(safe-area-inset-bottom)]',
            itemsMovil.length === 3
              ? 'grid-cols-3'
              : itemsMovil.length === 4
                ? 'grid-cols-4'
                : 'grid-cols-5',
          )}
          aria-label={t('nav.main')}
        >
          {itemsMovil.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  'flex min-w-0 flex-col items-center gap-0.5 px-1 py-2.5 text-[11px] leading-tight transition',
                  isActive ? 'text-primary-soft' : 'text-muted',
                )
              }
            >
              <span aria-hidden="true" className="text-base">
                {item.icon}
              </span>
              <span className="w-full truncate text-center">{t(item.corto ?? item.label)}</span>
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------ */
/* Grupos del menú                                                            */
/* ------------------------------------------------------------------------ */

/** Dónde se recuerda qué grupos dejó plegados el usuario. */
const CLAVE_PLEGADOS = '29100.nav.collapsed';

function leerPlegados(): Set<NavGroup> {
  try {
    const guardado: unknown = JSON.parse(localStorage.getItem(CLAVE_PLEGADOS) ?? '[]');
    return new Set(Array.isArray(guardado) ? (guardado as NavGroup[]) : []);
  } catch {
    // Un valor corrupto no puede dejar sin menú a nadie: se abre todo.
    return new Set();
  }
}

function guardarPlegados(grupos: Set<NavGroup>) {
  try {
    localStorage.setItem(CLAVE_PLEGADOS, JSON.stringify([...grupos]));
  } catch {
    // Modo privado o almacenamiento lleno: el menú sigue funcionando, sólo
    // deja de recordar el plegado entre visitas.
  }
}

/**
 * Un grupo del menú lateral.
 *
 * Los grupos sin etiqueta —lo de diario arriba, los ajustes abajo— se pintan
 * sueltos: una cabecera «Principal» sobre «Inicio» y «Mis archivos» ocupa una
 * línea para no decir nada.
 *
 * El plegado es un botón aparte del enlace a la ventana de elección, y no la
 * cabecera entera: si toda la fila plegara, el grupo con ventana no tendría
 * forma de abrirse; y si toda la fila navegara, no habría forma de plegarlo.
 * Son dos acciones distintas y por eso son dos controles.
 */
function GrupoDelMenu({
  grupo,
  abierto,
  onAlternar,
  onNavegar,
}: {
  grupo: NavSection;
  abierto: boolean;
  onAlternar: () => void;
  onNavegar: () => void;
}) {
  const t = useT();
  const id = `nav-grupo-${grupo.id.toLowerCase()}`;

  const enlaces = grupo.items.map((item) => (
    <NavLink
      key={item.to}
      to={item.to}
      end={item.end}
      onClick={onNavegar}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-xl px-3 py-2 text-sm transition',
          isActive
            ? 'bg-brand/15 text-content shadow-[inset_0_0_0_1px_rgb(var(--c-primary)/25%)]'
            : 'text-muted hover:bg-surface-2/60 hover:text-content',
        )
      }
    >
      <span aria-hidden="true" className="w-5 text-center">
        {item.icon}
      </span>
      {t(item.label)}
    </NavLink>
  ));

  if (!grupo.label) return <div className="space-y-1">{enlaces}</div>;

  const etiqueta = t(grupo.label);

  return (
    <div className="pt-3">
      <div className="flex items-center gap-1">
        {grupo.hub ? (
          <NavLink
            to={grupo.hub}
            onClick={onNavegar}
            className={({ isActive }) =>
              cn(
                'flex min-w-0 flex-1 items-center gap-2 rounded-lg px-2 py-1.5 text-xs font-semibold uppercase tracking-wide transition',
                isActive ? 'text-primary-soft' : 'text-muted hover:text-content',
              )
            }
          >
            <span aria-hidden="true">{grupo.icon}</span>
            <span className="truncate">{etiqueta}</span>
          </NavLink>
        ) : (
          <span className="flex min-w-0 flex-1 items-center gap-2 px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted">
            <span aria-hidden="true">{grupo.icon}</span>
            <span className="truncate">{etiqueta}</span>
          </span>
        )}

        {/* Plegado: cuántas secciones esconde se dice con el número, para que
            plegar no sea perder de vista que ahí había algo. */}
        {abierto ? null : (
          <span aria-hidden="true" className="text-[11px] tabular-nums text-muted">
            {grupo.items.length}
          </span>
        )}

        <button
          type="button"
          aria-expanded={abierto}
          aria-controls={id}
          onClick={onAlternar}
          className="grid h-7 w-7 shrink-0 place-items-center rounded-lg text-muted transition hover:bg-surface-2/60 hover:text-content"
        >
          <span
            aria-hidden="true"
            className={cn('text-[10px] transition-transform', abierto ? '' : '-rotate-90')}
          >
            ▼
          </span>
          <span className="sr-only">
            {t(abierto ? 'nav.collapseGroup' : 'nav.expandGroup', { section: etiqueta })}
          </span>
        </button>
      </div>

      {/*
        Se esconde con el atributo `hidden`, no con una clase: `display:none`
        pintado por CSS deja el enlace en el árbol de accesibilidad si la hoja
        de estilos no ha cargado, y un lector de pantalla acabaría leyendo las
        secciones de un grupo plegado.

        La guía a la izquierda es lo que hace que las secciones se lean como
        hijas de la cabecera y no como una lista más que empieza debajo.
      */}
      <div id={id} hidden={!abierto} className="ml-2 space-y-1 border-l border-line/40 pl-1.5">
        {enlaces}
      </div>
    </div>
  );
}
